import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, Pencil, Plus, ReceiptIndianRupee, Save, Share2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { TableSurface, EmptyState } from "@/components/CommandSystem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, inr } from "@/lib/format";
import { hasAnyPermission } from "@/lib/permissions";
import { BranchSettings } from "@/pages/Branches";

const EMPTY = {
  charge_type: "printing",
  margin_type: "percent",
  margin_value: "",
  effective_from: new Date().toISOString().slice(0, 10),
};

const ACCOUNTING_EMPTY = {
  default_gst_mode: "exclusive",
  default_gst_percentage: 18,
  default_cgst_percentage: 9,
  default_sgst_percentage: 9,
  default_igst_percentage: 18,
  default_invoice_type: "tax_invoice",
  default_payment_due_days: 30,
  default_place_of_supply: "",
  default_state: "Maharashtra",
  default_round_off: "none",
};

function labelForCharge(type) {
  return type === "installation" ? "Installation" : "Printing";
}

function formatMargin(row) {
  const value = Number(row.margin_value || 0);
  return row.margin_type === "percent" ? `${value}%` : inr(value);
}

function partnerNetworkWarning(settings) {
  const shared = Number(settings?.shared_inventory_count || 0);
  const taken = Number(settings?.taken_inventory_count || 0);
  const parts = [];
  if (shared > 0) parts.push(`${shared} shared inventory listing${shared === 1 ? "" : "s"}`);
  if (taken > 0) parts.push(`${taken} partner inventory listing${taken === 1 ? "" : "s"} in your network`);
  return parts.length ? `Partner Network cannot be turned off while you have ${parts.join(" and ")}.` : "";
}

export function Business() {
  const { user } = useAuth();
  const [margins, setMargins] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [accounting, setAccounting] = useState(ACCOUNTING_EMPTY);
  const [partnerSettings, setPartnerSettings] = useState(null);
  const [savingPartnerSettings, setSavingPartnerSettings] = useState(false);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [mediaTypeName, setMediaTypeName] = useState("");
  const [editingTypeId, setEditingTypeId] = useState("");
  const [editingTypeName, setEditingTypeName] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingAccounting, setSavingAccounting] = useState(false);
  const [savingMediaType, setSavingMediaType] = useState(false);
  const canManageBusinessFinance = hasAnyPermission(user, ["edit_campaign_amounts", "change_campaign_amounts"]);
  const canManagePartnerNetwork = hasAnyPermission(user, ["manage_partner_network"]);
  const canManageHoardingTypes = hasAnyPermission(user, ["create_hoardings", "edit_all_hoardings", "modify_records"]);
  const canViewBranches = hasAnyPermission(user, ["invite_employees", "edit_employee_permissions", "view_financial_reports", "export_reports"]);

  async function loadMargins() {
    try {
      const { data } = await api.get("/business/margins", { skipGlobalLoader: true });
      setMargins(Array.isArray(data) ? data : []);
    } catch {
      setMargins([]);
    }
  }

  useEffect(() => {
    if (!canManageBusinessFinance) return;
    loadMargins();
    api.get("/business/accounting-settings", { skipGlobalLoader: true })
      .then(({ data }) => setAccounting({ ...ACCOUNTING_EMPTY, ...(data || {}) }))
      .catch(() => setAccounting(ACCOUNTING_EMPTY));
  }, [canManageBusinessFinance]);

  useEffect(() => {
    if (!canManagePartnerNetwork) return;
    api.get("/partner-network/settings", { skipGlobalLoader: true })
      .then(({ data }) => setPartnerSettings(data || {}))
      .catch(() => setPartnerSettings({ enabled: false, share_inventory_enabled: true, use_partner_inventory_enabled: true }));
  }, [canManagePartnerNetwork]);

  async function loadMediaTypes() {
    try {
      const { data } = await api.get("/business/hoarding-types", { params: { include_inactive: "true" }, skipGlobalLoader: true });
      setMediaTypes(Array.isArray(data) ? data : []);
    } catch {
      setMediaTypes([]);
    }
  }

  useEffect(() => {
    if (!canManageHoardingTypes) return;
    loadMediaTypes();
  }, [canManageHoardingTypes]);

  const currentMargins = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const latest = {};
    margins
      .filter((m) => !m.effective_from || m.effective_from <= today)
      .sort((a, b) => String(b.effective_from || "").localeCompare(String(a.effective_from || "")))
      .forEach((m) => {
        if (!latest[m.charge_type]) latest[m.charge_type] = m;
      });
    return latest;
  }, [margins]);

  async function save(e) {
    e.preventDefault();
    const value = Number(form.margin_value || 0);
    if (form.margin_type === "percent" && value > 100) {
      toast.error("Margin percentage cannot be more than 100%.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/business/margins", { ...form, margin_value: value });
      toast.success("Business margin saved");
      setForm({ ...EMPTY, charge_type: form.charge_type });
      await loadMargins();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveAccounting(e) {
    e.preventDefault();
    setSavingAccounting(true);
    try {
      await api.put("/business/accounting-settings", {
        ...accounting,
        default_gst_percentage: Number(accounting.default_gst_percentage || 0),
        default_cgst_percentage: Number(accounting.default_cgst_percentage || 0),
        default_sgst_percentage: Number(accounting.default_sgst_percentage || 0),
        default_igst_percentage: Number(accounting.default_igst_percentage || 0),
        default_payment_due_days: Number(accounting.default_payment_due_days || 0),
      });
      toast.success("Accounting defaults saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingAccounting(false);
    }
  }

  async function updatePartnerSettings(patch) {
    setSavingPartnerSettings(true);
    const previous = partnerSettings || {};
    const optimistic = { ...previous, ...patch };
    setPartnerSettings(optimistic);
    try {
      const { data } = await api.put("/partner-network/settings", patch);
      setPartnerSettings(data || {});
      if (Object.prototype.hasOwnProperty.call(patch, "enabled")) {
        window.dispatchEvent(new CustomEvent("partner-network-sidebar-setting-changed", { detail: { showInSidebar: !!patch.enabled } }));
      }
      toast.success("Partner Network preference saved");
    } catch (err) {
      setPartnerSettings(previous);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPartnerSettings(false);
    }
  }

  function togglePartnerInterest(enabled) {
    const share = !!partnerSettings?.share_inventory_enabled;
    const use = !!partnerSettings?.use_partner_inventory_enabled;
    if (enabled && !share && !use) {
      updatePartnerSettings({ enabled, show_in_sidebar: enabled, share_inventory_enabled: true, use_partner_inventory_enabled: true });
      return;
    }
    updatePartnerSettings({ enabled, show_in_sidebar: enabled });
  }

  async function addMediaType(e) {
    e.preventDefault();
    if (!mediaTypeName.trim()) {
      toast.error("Media type name is required");
      return;
    }
    setSavingMediaType(true);
    try {
      await api.post("/business/hoarding-types", { name: mediaTypeName.trim() });
      toast.success("Media type added");
      setMediaTypeName("");
      await loadMediaTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingMediaType(false);
    }
  }

  async function renameMediaType(type) {
    if (!editingTypeName.trim()) {
      toast.error("Media type name is required");
      return;
    }
    setSavingMediaType(true);
    try {
      await api.put(`/business/hoarding-types/${type.id}`, { name: editingTypeName.trim() });
      toast.success("Media type renamed");
      setEditingTypeId("");
      setEditingTypeName("");
      await loadMediaTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingMediaType(false);
    }
  }

  async function toggleMediaType(type) {
    setSavingMediaType(true);
    try {
      await api.put(`/business/hoarding-types/${type.id}`, { is_active: !type.is_active });
      toast.success(type.is_active ? "Media type disabled" : "Media type enabled");
      await loadMediaTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingMediaType(false);
    }
  }

  async function deleteMediaType(type) {
    if (!window.confirm(type.used_count > 0 ? "This type is used by inventory and will be disabled instead. Continue?" : "Delete this media type?")) return;
    setSavingMediaType(true);
    try {
      await api.delete(`/business/hoarding-types/${type.id}`);
      toast.success(type.used_count > 0 ? "Media type disabled" : "Media type deleted");
      await loadMediaTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingMediaType(false);
    }
  }

  return (
    <Layout title="Business Settings" subtitle="Set margins, accounting defaults, and branch visibility from one place.">
      <div className="space-y-6">
        {canManageHoardingTypes && (
          <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-5">
            <form onSubmit={addMediaType} className="hx-card p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                  <BriefcaseBusiness size={20} />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-slate-950">Hoarding Types</h3>
                  <p className="text-xs text-slate-500">Names used in inventory, campaign filters, and proposals.</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="hx-eyebrow">Type Name</Label>
                <Input value={mediaTypeName} onChange={(e) => setMediaTypeName(e.target.value)} placeholder="Hoarding, Billboard, Bus Shelter" />
              </div>
              <Button disabled={savingMediaType} className="w-full">
                <Plus size={16} /> {savingMediaType ? "Saving..." : "Add type"}
              </Button>
            </form>

            <div className="hx-card overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="font-display font-semibold text-slate-950">Configured Types</h3>
              </div>
              <TableSurface className="overflow-x-auto rounded-none border-0">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Inventory</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mediaTypes.map((type) => (
                      <tr key={type.id}>
                        <td className="font-medium text-slate-800">
                          {editingTypeId === type.id ? (
                            <Input value={editingTypeName} onChange={(e) => setEditingTypeName(e.target.value)} className="h-9" autoFocus />
                          ) : type.name}
                        </td>
                        <td className="text-slate-600">{type.used_count || 0} used</td>
                        <td>
                          <button type="button" onClick={() => toggleMediaType(type)} disabled={savingMediaType} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${type.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                            {type.is_active ? "Active" : "Disabled"}
                          </button>
                        </td>
                        <td>
                          <div className="flex justify-end gap-2">
                            {editingTypeId === type.id ? (
                              <>
                                <button type="button" onClick={() => renameMediaType(type)} disabled={savingMediaType} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50" title="Save">
                                  <Save size={14} />
                                </button>
                                <button type="button" onClick={() => { setEditingTypeId(""); setEditingTypeName(""); }} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50" title="Cancel">
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <button type="button" onClick={() => { setEditingTypeId(type.id); setEditingTypeName(type.name); }} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50" title="Rename">
                                <Pencil size={14} />
                              </button>
                            )}
                            <button type="button" onClick={() => deleteMediaType(type)} disabled={savingMediaType} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50" title={type.used_count > 0 ? "Disable" : "Delete"}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {mediaTypes.length === 0 && (
                      <tr>
                        <td colSpan="4"><EmptyState title="No hoarding types yet" description="Add the inventory names this business uses." /></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </TableSurface>
            </div>
          </div>
        )}

        {canManageBusinessFinance && (
        <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-5">
          <div className="space-y-5">
          <form onSubmit={save} className="hx-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
              <BriefcaseBusiness size={20} />
            </div>
            <div>
              <h3 className="font-display font-semibold text-slate-950">Margin Rule</h3>
              <p className="text-xs text-slate-500">Rules apply to campaigns starting from the effective date.</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="hx-eyebrow">Charge Type</Label>
            <Select value={form.charge_type} onValueChange={(v) => setForm({ ...form, charge_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="printing">Printing</SelectItem>
                <SelectItem value="installation">Installation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-[1fr_140px] gap-3">
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">Margin Type</Label>
              <Select value={form.margin_type} onValueChange={(v) => setForm({ ...form, margin_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage</SelectItem>
                  <SelectItem value="amount">Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">{form.margin_type === "percent" ? "Percent" : "Amount"}</Label>
              <Input type="number" min="0" max={form.margin_type === "percent" ? 100 : undefined} value={form.margin_value} onChange={(e) => setForm({ ...form, margin_value: e.target.value })} placeholder="0" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="hx-eyebrow">Effective From</Label>
            <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
          </div>

          <Button disabled={saving} className="w-full">
            <Plus size={16} /> {saving ? "Saving..." : "Save margin"}
          </Button>
          </form>

          <form onSubmit={saveAccounting} className="hx-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <ReceiptIndianRupee size={20} />
            </div>
            <div>
              <h3 className="font-display font-semibold text-slate-950">GST & Accounting Defaults</h3>
              <p className="text-xs text-slate-500">Used to prefill proposals, campaigns, and invoices.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">GST Mode</Label>
              <Select value={accounting.default_gst_mode} onValueChange={(v) => setAccounting({ ...accounting, default_gst_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="exclusive">Exclusive</SelectItem><SelectItem value="inclusive">Inclusive</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">GST %</Label>
              <Input type="number" min="0" value={accounting.default_gst_percentage} onChange={(e) => setAccounting({ ...accounting, default_gst_percentage: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">CGST %</Label>
              <Input type="number" min="0" value={accounting.default_cgst_percentage} onChange={(e) => setAccounting({ ...accounting, default_cgst_percentage: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">SGST %</Label>
              <Input type="number" min="0" value={accounting.default_sgst_percentage} onChange={(e) => setAccounting({ ...accounting, default_sgst_percentage: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">IGST %</Label>
              <Input type="number" min="0" value={accounting.default_igst_percentage} onChange={(e) => setAccounting({ ...accounting, default_igst_percentage: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">Due Days</Label>
              <Input type="number" min="0" value={accounting.default_payment_due_days} onChange={(e) => setAccounting({ ...accounting, default_payment_due_days: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">Invoice Type</Label>
              <Select value={accounting.default_invoice_type} onValueChange={(v) => setAccounting({ ...accounting, default_invoice_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tax_invoice">Tax Invoice</SelectItem><SelectItem value="proforma_invoice">Proforma Invoice</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="hx-eyebrow">Round Off</Label>
              <Select value={accounting.default_round_off} onValueChange={(v) => setAccounting({ ...accounting, default_round_off: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="nearest_rupee">Nearest rupee</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="hx-eyebrow">Default State / Place of Supply</Label>
            <Input value={accounting.default_state} onChange={(e) => setAccounting({ ...accounting, default_state: e.target.value, default_place_of_supply: e.target.value })} />
          </div>
          <Button disabled={savingAccounting} className="w-full">
            <Plus size={16} /> {savingAccounting ? "Saving..." : "Save accounting defaults"}
          </Button>
          </form>
          </div>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["printing", "installation"].map((type) => {
              const row = currentMargins[type];
              return (
                <div key={type} className="hx-card p-5">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                      <ReceiptIndianRupee size={18} />
                    </div>
                    <div>
                      <div className="hx-eyebrow">{labelForCharge(type)} current margin</div>
                      <div className="font-display text-xl font-semibold text-slate-950">{row ? formatMargin(row) : "Not set"}</div>
                    </div>
                  </div>
                  {row && <p className="mt-3 text-xs text-slate-500">Effective from {fmtDate(row.effective_from)}</p>}
                </div>
              );
            })}
            </div>

            <div className="hx-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-display font-semibold text-slate-950">Margin History</h3>
            </div>
            <TableSurface className="overflow-x-auto rounded-none border-0">
              <table>
                <thead>
                  <tr>
                    <th>Charge</th>
                    <th>Margin</th>
                    <th>Effective From</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {margins.map((row) => (
                    <tr key={row.id}>
                      <td className="font-medium text-slate-800">{labelForCharge(row.charge_type)}</td>
                      <td className="hx-money-cell">{formatMargin(row)}</td>
                      <td className="hx-date-cell">{fmtDate(row.effective_from)}</td>
                      <td className="hx-date-cell">{fmtDate(row.created_at)}</td>
                    </tr>
                  ))}
                  {margins.length === 0 && (
                    <tr>
                      <td colSpan="4"><EmptyState title="No margin rules yet" description="Save a margin rule to see history here." /></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableSurface>
            </div>
          </div>
        </div>
        )}

        {canViewBranches && <BranchSettings showHeader={false} />}

        {canManagePartnerNetwork && (
          <div className="hx-card p-5">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
              <div className="flex min-w-0 gap-3">
                <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center flex-shrink-0">
                  <Share2 size={20} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display font-semibold text-slate-950">Interested in Partner Network</h3>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Choose how this business wants to use partner inventory so the app only shows the matching workflow.
                  </p>
                  {partnerNetworkWarning(partnerSettings) && (
                    <p className="mt-2 text-xs font-semibold text-amber-700">{partnerNetworkWarning(partnerSettings)}</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4 xl:min-w-[520px]">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-sm font-bold text-slate-700">Turn on Partner Network</Label>
                  <Switch
                    checked={!!partnerSettings?.enabled}
                    disabled={savingPartnerSettings || !partnerSettings}
                    onCheckedChange={togglePartnerInterest}
                  />
                </div>

                {!!partnerSettings?.enabled && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={!!partnerSettings?.share_inventory_enabled}
                        disabled={savingPartnerSettings}
                        onChange={(e) => updatePartnerSettings({ share_inventory_enabled: e.target.checked })}
                      />
                      <span>
                        <span className="block text-sm font-bold text-slate-800">Share my inventory</span>
                        <span className="mt-1 block text-xs leading-5 text-slate-500">Connect to hosts, offer your hoardings, and approve usage requests.</span>
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={!!partnerSettings?.use_partner_inventory_enabled}
                        disabled={savingPartnerSettings}
                        onChange={(e) => updatePartnerSettings({ use_partner_inventory_enabled: e.target.checked })}
                      />
                      <span>
                        <span className="block text-sm font-bold text-slate-800">Use partner inventory</span>
                        <span className="mt-1 block text-xs leading-5 text-slate-500">Share a code, receive partner offers, and use approved partner sites in proposals.</span>
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
